// Theme Provider Component
// React context provider for design system theme

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, lightTheme, darkTheme } from './theme';

interface ThemeContextType {
  theme: Theme;
  mode: 'light' | 'dark' | 'system';
  toggleTheme: () => void;
  setTheme: (mode: 'light' | 'dark' | 'system') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: 'light' | 'dark' | 'system';
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'system'
}) => {
  const [mode, setMode] = useState<'light' | 'dark' | 'system'>(defaultMode);
  const [systemPrefersDark, setSystemPrefersDark] = useState(false);

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemPrefersDark(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Load saved theme preference
  useEffect(() => {
    const savedMode = localStorage.getItem('minecraft-portal-theme') as 'light' | 'dark' | 'system';
    if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
      setMode(savedMode);
    }
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('minecraft-portal-theme', mode);
  }, [mode]);

  // Determine active theme
  const getActiveTheme = (): Theme => {
    if (mode === 'system') {
      return systemPrefersDark ? darkTheme : lightTheme;
    }
    return mode === 'dark' ? darkTheme : lightTheme;
  };

  const theme = getActiveTheme();

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const isValidMode = theme.mode === 'dark';
    
    // Set CSS custom properties for theme colors
    root.style.setProperty('--color-primary', theme.colors.primary[500]);
    root.style.setProperty('--color-success', theme.colors.success[500]);
    root.style.setProperty('--color-warning', theme.colors.warning[500]);
    root.style.setProperty('--color-danger', theme.colors.danger[500]);
    
    root.style.setProperty('--color-background-primary', theme.semanticColors.background.primary);
    root.style.setProperty('--color-background-secondary', theme.semanticColors.background.secondary);
    root.style.setProperty('--color-surface-primary', theme.semanticColors.surface.primary);
    root.style.setProperty('--color-border-primary', theme.semanticColors.border.primary);
    root.style.setProperty('--color-text-primary', theme.semanticColors.text.primary);
    root.style.setProperty('--color-text-secondary', theme.semanticColors.text.secondary);

    // Set font family
    root.style.setProperty('--font-family-sans', theme.typography.fontFamily.sans);
    root.style.setProperty('--font-family-mono', theme.typography.fontFamily.mono);

    // Set dark/light mode class
    if (isValidMode) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }

    // Set color scheme for better browser integration
    root.style.colorScheme = theme.mode;
  }, [theme]);

  const toggleTheme = () => {
    setMode(prevMode => {
      if (prevMode === 'light') return 'dark';
      if (prevMode === 'dark') return 'system';
      return 'light';
    });
  };

  const setTheme = (newMode: 'light' | 'dark' | 'system') => {
    setMode(newMode);
  };

  const value: ThemeContextType = {
    theme,
    mode,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using theme
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Higher-order component for theme injection (removed for TypeScript compatibility)
// Use useTheme() hook directly in components instead